/**
 * PL/SQL Debugger tab content types, factories, and helpers.
 *
 * These types are feature-local — imported by debugger hooks and components
 * directly. workspaceTypes.ts re-exports them so existing callers outside the
 * feature folder continue to work without import changes.
 */

import type {
  DebugSessionState,
  DebugBreakpoint,
  DebugVariable,
  DebugStackFrame,
  OracleDebuggerCompilationError,
  OracleProfilerLineData,
  OracleProfilerUnitSummary,
} from '../../../types/electron';

import type { PackageMemberParam } from '../core/types/workspaceTypes';

// ---------------------------------------------------------------------------
// Core types
// ---------------------------------------------------------------------------

/** Debug object type for the PL/SQL debugger */
export type PlSqlDebuggerObjectType = 'procedure' | 'function' | 'package' | 'trigger' | 'anonymous';

/**
 * Frontend debug state — a subset of the backend DebugSessionState.
 * Excludes 'attached' which is a backend-only transitional state
 * between init and the first SYNCHRONIZE call.
 */
export type PlSqlDebugState = Exclude<DebugSessionState, 'attached'>;

/** Profiling state for the debugger tab */
export type ProfilingState = 'idle' | 'running' | 'complete';

/**
 * Identifies a program unit for per-program breakpoint ownership.
 * Used to key breakpoints by their owning program so source switching
 * renders only the correct breakpoints.
 */
export type ProgramIdentity = {
  owner: string;
  name: string;
  namespace: number;
};

/** Content for PL/SQL debugger tabs */
export type PlSqlDebuggerTabContent = {
  /** Object name being debugged */
  objectName: string;
  /** Schema (owner) */
  schema: string;
  /** Database */
  database: string;
  /** Object type */
  objectType: PlSqlDebuggerObjectType;
  /** PL/SQL source code */
  source?: string;
  /** Debug session key (from backend) */
  sessionKey?: string;
  /** Current debug state */
  debugState: PlSqlDebugState;
  /** Current execution line (when paused) */
  currentLine?: number;
  /** Breakpoints for the currently displayed program (view into breakpointsByProgram) */
  breakpoints: DebugBreakpoint[];
  /**
   * Per-program breakpoint storage keyed by programKey(owner.name.namespace).
   * Source of truth for all breakpoints across stepped-into routines.
   */
  breakpointsByProgram: Record<string, DebugBreakpoint[]>;
  /**
   * Currently displayed program identity (differs from tab's objectName/schema
   * when stepping into another routine or selecting a different call stack frame).
   */
  displayedProgram?: ProgramIdentity;
  /** Variables (populated when paused) */
  variables: DebugVariable[];
  /** Call stack frames */
  callStack: DebugStackFrame[];
  /** Messages (output, errors) */
  messages: Array<{ type: 'info' | 'error' | 'output'; content: string; timestamp: string }>;
  /** Watch expressions */
  watchExpressions: Array<{ id: string; expression: string; value?: string | null; error?: string }>;
  /** Exception breakpoint settings */
  exceptionBreakpoints: {
    breakOnAll: boolean;
    namedExceptions: string[];
  };
  /** Debug protocol used by the active session ('dbms_debug' or 'jdwp') */
  debugProtocol?: 'dbms_debug' | 'jdwp';
  /** Error message */
  error?: string;
  /** Loading state */
  isLoading?: boolean;
  /**
   * Selected package member name (set when debugger is opened from a package
   * member context menu or Package Browser member click). Used to pre-fill
   * the start dialog with a member-specific call template.
   */
  targetMember?: { name: string; type: 'procedure' | 'function'; overloadIndex?: number; params?: PackageMemberParam[]; returnType?: string };
  /** Package members (procedures/functions) — fetched for packages to populate the start dialog */
  members?: Array<{ name: string; type: 'procedure' | 'function'; overloadIndex?: number; params?: PackageMemberParam[]; returnType?: string }>;
  /**
   * The last PL/SQL block successfully used to start a debug session.
   * Enables "Restart Last Call" without re-editing the start dialog.
   */
  lastLaunchBlock?: string;

  // --- Profiling ---
  /** Current profiling state */
  profilingState: ProfilingState;
  /** Active profile key for cleanup */
  profileKey?: string;
  /** Profile run ID for fetching results */
  profileRunId?: number;
  /** Line-level profiling results */
  profileLines?: OracleProfilerLineData[];
  /** Unit summaries for the profiling run */
  profileUnits?: OracleProfilerUnitSummary[];
  /** Total run time in nanoseconds */
  profileTotalRunTime?: number;
  /** Execution error from profiled PL/SQL block */
  profileExecutionError?: string;

  // --- Compilation errors ---
  /** Structured compilation errors from the last compile-for-debug attempt */
  debugCompileErrors?: OracleDebuggerCompilationError[];
};

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

/** Create default content for a PL/SQL debugger tab */
export function createDefaultPlSqlDebuggerContent(
  objectName: string,
  schema: string,
  database: string,
  objectType: PlSqlDebuggerObjectType = 'procedure',
): PlSqlDebuggerTabContent {
  return {
    objectName,
    schema,
    database,
    objectType,
    debugState: 'idle',
    breakpoints: [],
    breakpointsByProgram: {},
    variables: [],
    callStack: [],
    messages: [],
    watchExpressions: [],
    exceptionBreakpoints: { breakOnAll: false, namedExceptions: [] },
    isLoading: true,
    profilingState: 'idle',
  };
}
